/**
 * Offline fallback for the song page. Normally the song screen reads ['song', id] (persisted query
 * cache), but that cache can lag behind (iOS may kill the app right after "Vista fyrir ónettengt").
 * The offline manifest kept by the pack manager is the source of truth for what is on the device,
 * so when the network is unreachable and the query has no data, the song is rebuilt from it
 * (titles come from the persisted ['songs'] list when available).
 */
import { useSyncExternalStore } from 'react';
import type { SongDTO, SongFileDTO, SongListItemDTO, TierInfo } from '../../shared/dto/library';
import type { ManifestFile } from '../../shared/dto/offline';
import { queryClient } from '../api/queryClient';
import { data, getSnapshot, subscribe } from './store';

function tier(f: ManifestFile, t: 's' | 'm' | 'l'): TierInfo | undefined {
  const p = f.parts[t];
  return p ? { size: p.size, etag: p.etag } : undefined;
}

function toFileDTO(f: ManifestFile, sort: number, updatedAt: number): SongFileDTO {
  const tiers = f.kind === 'sheet' ? { s: tier(f, 's'), m: tier(f, 'm'), l: tier(f, 'l') } : null;
  return {
    id: f.id,
    songId: f.songId,
    kind: f.kind,
    title: f.title,
    audienceMask: f.audienceMask,
    rev: f.rev,
    sort,
    updatedAt,
    origName: null,
    origMime: f.parts.orig?.mime ?? null,
    origSize: f.parts.orig?.size ?? null,
    pageCount: f.pages?.length ?? null,
    pages: f.pages,
    tiers: tiers ? (Object.fromEntries(Object.entries(tiers).filter(([, v]) => v)) as SongFileDTO['tiers']) : null,
    durationMs: null,
    lyricsText: f.lyricsText,
  };
}

const memo = new Map<string, { manifest: object; list: unknown; song: SongDTO | null }>();

/** Builds a SongDTO for `songId` from the stored offline manifest, or null if it isn't there. */
export function offlineSong(songId: string): SongDTO | null {
  const m = data.manifest;
  if (!m || !data.user) return null;
  const list = queryClient.getQueryData(['songs']);
  const hit = memo.get(songId);
  if (hit && hit.manifest === m && hit.list === list) return hit.song;
  const song = buildSong(songId, m, list as SongListItemDTO[] | undefined);
  memo.set(songId, { manifest: m, list, song });
  return song;
}

function buildSong(songId: string, m: NonNullable<typeof data.manifest>, list: SongListItemDTO[] | undefined): SongDTO | null {
  const fileIds = m.songs[songId];
  if (!fileIds || fileIds.length === 0) return null;
  const byId = new Map(m.files.map((f) => [f.id, f]));
  const files = fileIds.map((id) => byId.get(id)).filter((f): f is ManifestFile => !!f);
  if (files.length === 0) return null;
  const item = list?.find((s) => s.id === songId);
  const updatedAt = item?.updatedAt ?? m.generatedAt;
  return {
    id: songId,
    title: item?.title ?? files[0]!.title,
    composer: item?.composer ?? null,
    arranger: item?.arranger ?? null,
    lyricist: null,
    notes: null,
    updatedAt,
    files: files.map((f, i) => toFileDTO(f, i, updatedAt)),
    collectionIds: item?.collectionIds ?? [],
  };
}

/** Re-renders when the offline store changes (the manifest loads asynchronously on start). */
export function useOfflineSong(songId: string | undefined, enabled: boolean): SongDTO | null {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return enabled && songId ? offlineSong(songId) : null;
}
